import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Database } from '@/types/supabase'
import { AvatarUpload } from './avatar-upload'
import { useAuth } from '@/context/auth-context'
import { profileSchema, ProfileFormValues } from './schemas'

function ProfileForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [avatarError, setAvatarError] = useState<string | null>(null)

  // Use Auth Context instead of Redux
  const { profile, updateProfile, success: authSuccess } = useAuth()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: profile?.username || '',
      avatar_url: profile?.avatar_url || '',
      bio: profile?.bio || '',
      location: profile?.location || '',
    },
  })

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      setIsLoading(true)
      setError(null)

      // Only include fields that are defined
      const updatedProfile: Partial<Database['public']['Tables']['profiles']['Update']> = {}

      if (data.username) updatedProfile.username = data.username
      if (data.avatar_url) updatedProfile.avatar_url = data.avatar_url
      if (data.bio !== undefined) updatedProfile.bio = data.bio
      if (data.location !== undefined) updatedProfile.location = data.location

      const { error: updateError } = await updateProfile(updatedProfile)

      if (updateError) {
        throw new Error(updateError)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAvatarUploadSuccess = (url: string) => {
    // Add a timestamp to the URL to prevent caching
    const timestamp = new Date().getTime()
    const urlWithTimestamp = url.includes('?') ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`

    setValue('avatar_url', urlWithTimestamp)
    setAvatarError(null)
  }

  const handleAvatarUploadError = (error: string) => {
    setAvatarError(error)
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-6 space-y-6 bg-background/50 rounded-xl shadow-lg border border-gray-800">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text">Your Profile</h1>
        <p className="text-text/70 mt-2">Update your personal information</p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}

      {authSuccess && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md text-green-500 text-sm">
          {authSuccess}
        </div>
      )}

      {avatarError && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {avatarError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Avatar upload component */}
          <AvatarUpload
            onUploadSuccess={handleAvatarUploadSuccess}
            onUploadError={handleAvatarUploadError}
          />

          {/* Form fields */}
          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium text-text">
                Username
              </label>
              <input
                id="username"
                type="text"
                placeholder="boardgamer123"
                className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                {...register('username')}
              />
              {errors.username && (
                <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="bio" className="text-sm font-medium text-text">
                Bio
              </label>
              <textarea
                id="bio"
                placeholder="Tell us about yourself..."
                className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
                {...register('bio')}
              />
              {errors.bio && <p className="text-red-500 text-xs mt-1">{errors.bio.message}</p>}
            </div>

            <div className="space-y-2">
              <label htmlFor="location" className="text-sm font-medium text-text">
                Location
              </label>
              <input
                id="location"
                type="text"
                placeholder="City, Country"
                className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                {...register('location')}
              />
              {errors.location && (
                <p className="text-red-500 text-xs mt-1">{errors.location.message}</p>
              )}
            </div>

            <div className="hidden">
              <input id="avatar_url" type="text" {...register('avatar_url')} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

export { ProfileForm }
